import uniq from 'lodash/uniq';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RequestAction } from '@transcend-io/privacy-types';

import type { ApolloError } from '@main/core-ui';
import { message } from '@main/core-ui';
import { ACTIONS } from '@main/datamap-types';
import { RequestHistory } from '@main/dsr-types';
import { endpoints } from '@main/pc-types';
import { setDisabledActions } from '@main/privacy-center/src/containers/TakeControl/slice';
import { buildUseDhQuery } from '@main/privacy-center/src/hooks';
import { OrderDirection } from '@main/schema-utils';
import { ONE_MINUTE } from '@main/utils';

import {
  selectActiveRequests,
  selectDataSubjectActiveCount,
} from './selectors';
import { setActiveCount, setActiveRequests, setRequestId } from './slice';

const useRequestHistory = buildUseDhQuery(endpoints.requestHistory);
const useRequestHistoryActiveCount = buildUseDhQuery(
  endpoints.requestHistory,
  'RequestHistoryActiveCount',
  {
    totalCount: null,
    nodes: {
      id: null,
      type: null,
      canDownload: null,
      canViewByDataSubject: null,
      isClosed: null,
    },
  },
);

// Get the actions that should be disabled
// TODO: https://github.com/transcend-io/main/issues/2758 - should not be needed once access and erasure are split
const getActions = (type: RequestAction): RequestAction[] => {
  const action = ACTIONS[type];
  return action.delay ? [action.delay.previewAction, type] : [type];
};

/** Display this many per page */
const PAGE_SIZE = 12;

/** Can never have more than this many open requests */
const MAX_ACTIVE = Object.values(RequestAction).length;

/**
 * Fetch request history and cache to redux
 *
 * @returns Request history data to render
 */
export function useRequestHistoryData(): {
  /** Fetch the next page */
  fetchMore?: () => void;
  /** Whether queries are being fetched for the first time */
  loading: boolean;
  /** When true, all active and inactive requests are show, else only active is shown */
  showAllRequests: boolean;
  /** The number of active data subject requests */
  dataSubjectActiveCount: number;
  /** The requests to display on the screen */
  activeRequests: RequestHistory[];
  /** An error if one occurred */
  error?: ApolloError;
  /** Callback to change where all requests or all active requests should be shown */
  setShowAllRequests: (showAll: boolean) => void;
} {
  const dispatch = useDispatch();
  const activeRequests = useSelector(selectActiveRequests);
  const dataSubjectActiveCount = useSelector(selectDataSubjectActiveCount);

  // determine whether we want to show all requests
  // of only active requests
  const [showAllRequests, setShowAllRequests] = useState(false);

  // fetch the history of data subject requests
  const { fetchMore, loading, data, error, startPolling, stopPolling } =
    useRequestHistory({
      variables: {
        first: PAGE_SIZE,
        filterBy: showAllRequests ? undefined : { isActive: true },
      },
    });
  const [currentPage, setCurrentPage] = useState(1);

  // if there are outstanding requests, poll every minute for 10 minutes
  useEffect(() => {
    if (dataSubjectActiveCount > 0) {
      startPolling(ONE_MINUTE);
      const timeout = setTimeout(() => {
        stopPolling();
      }, ONE_MINUTE * 10);
      return () => {
        clearTimeout(timeout);
        stopPolling();
      };
    }
    stopPolling();
    return undefined;
  }, [dataSubjectActiveCount, startPolling, stopPolling]);

  // when there are no active requests, update the popover
  // to show the request history
  useEffect(() => {
    if (!showAllRequests && data && data.nodes.length === 0) {
      setShowAllRequests(true);
    }
  }, [showAllRequests, data, setShowAllRequests]);

  // The current page
  const handlePageTurn = (current: number): void => {
    // fetch more items and save to apollo cache
    fetchMore({
      variables: { offset: (current - 1) * PAGE_SIZE },
      updateQuery: (prev, { fetchMoreResult }) => {
        setCurrentPage(current);
        if (!fetchMoreResult) {
          return prev as any;
        }
        if (!prev) {
          return fetchMoreResult;
        }
        return {
          ...fetchMoreResult,
          nodes: [...prev.nodes, ...fetchMoreResult.nodes],
        };
      },
    });
  };

  // update store upon fetching requestHistory
  useEffect(() => {
    if (data) {
      dispatch(
        setActiveRequests({
          activeRequests: data.nodes,
        }),
      );
    }
  }, [data]);

  // fetch all active requests to display a badge count
  // and also to determine which actions cannot be re-submitted
  const dataSubjectActiveCountData = useRequestHistoryActiveCount({
    variables: {
      first: MAX_ACTIVE,
      filterBy: {
        isActive: true,
      },
      orderBy: [{ field: 'createdAt', direction: OrderDirection.DESC }],
    },
  });
  useEffect(() => {
    if (dataSubjectActiveCountData?.data) {
      // first set the active count
      dispatch(
        setActiveCount({
          dataSubjectActiveCount: dataSubjectActiveCountData.data.totalCount,
        }),
      );

      // next disable any actions that have already been submitted
      // so that the data subject is aware they cannot re-submit
      dispatch(
        setDisabledActions({
          disabledActions: uniq(
            dataSubjectActiveCountData.data.nodes
              .filter((request) => !request.isClosed)
              .map(({ type }) => getActions(type))
              .flat(),
          ),
        }),
      );

      // select the first viewable request in the list to show previews of
      const viewableRequest = dataSubjectActiveCountData.data.nodes.find(
        (request) => request.canViewByDataSubject,
      );
      dispatch(setRequestId({ requestId: viewableRequest?.id }));
    }
  }, [dataSubjectActiveCountData?.data]);

  // Flash errors
  const displayError = error || dataSubjectActiveCountData.error;
  useEffect(() => {
    if (displayError) {
      message.error(displayError.message);
    }
  }, [displayError]);
  return {
    activeRequests,
    showAllRequests,
    loading: loading || dataSubjectActiveCountData.loading,
    dataSubjectActiveCount,
    setShowAllRequests,
    fetchMore:
      data?.totalCount && data.totalCount > activeRequests.length
        ? () => handlePageTurn(currentPage + 1)
        : undefined,
  };
}
